import React, { useState, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";

const SEASON_STYLES = {
  spring: {
    bg: "bg-emerald-50",
    hover: "hover:bg-emerald-100",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    label: "Spring (Mar\u2013May)",
  },
  autumn: {
    bg: "bg-amber-50",
    hover: "hover:bg-amber-100",
    text: "text-amber-700",
    dot: "bg-amber-500",
    label: "Autumn (Sep\u2013Nov)",
  },
  winter: {
    bg: "bg-sky-50",
    hover: "hover:bg-sky-100",
    text: "text-sky-700",
    dot: "bg-sky-500",
    label: "Winter (Dec\u2013Feb)",
  },
};

function getSeason(m) {
  if (m >= 2 && m <= 4) return "spring";
  if (m >= 8 && m <= 10) return "autumn";
  if (m === 11 || m <= 1) return "winter";
  return null;
}

function parseAvailableMonths(str) {
  if (!str) return [];
  const map = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  const months = new Set();
  str.split(", ").forEach((r) => {
    const [a, b] = r.split("-");
    const s = map[a?.trim()?.toLowerCase()?.slice(0, 3)];
    const e = map[b?.trim()?.toLowerCase()?.slice(0, 3)];
    if (s === undefined || e === undefined) return;
    if (s <= e) {
      for (let i = s; i <= e; i++) months.add(i);
    } else {
      for (let i = s; i < 12; i++) months.add(i);
      for (let i = 0; i <= e; i++) months.add(i);
    }
  });
  return [...months];
}

const MONTHS_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const mergedFeatures = [
  { text: "Expert Sherpa guide with assistant guide", icon: "Users" },
  { text: "Best available accommodation throughout trek", icon: "Home" },
  { text: "All meals including snacks during trek", icon: "Coffee" },
  { text: "All permits, fees, and TIMS card", icon: "FileText" },
  { text: "Comprehensive medical kit with altitude medication", icon: "Heart" },
  { text: "Satellite phone & communication device", icon: "Phone" },
  { text: "Professional photography coverage", icon: "Camera" },
  { text: "Flexible & customizable itinerary", icon: "MapPin" },
  { text: "Private guide team for personalized service", icon: "Star" },
  { text: "Customized meal options available", icon: "Wind" },
];

const BookingSection = ({ trek }) => {
  const navigate = useNavigate();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);

  const availableMonths = useMemo(
    () => parseAvailableMonths(trek?.bestSeason),
    [trek?.bestSeason],
  );

  const seasonForView = getSeason(viewMonth);
  const isMonthAvailable = availableMonths.includes(viewMonth);
  const viewSeasonStyle = seasonForView ? SEASON_STYLES[seasonForView] : null;

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const cellDate = new Date(viewYear, viewMonth, d);
    cellDate.setHours(0, 0, 0, 0);
    const isPast = cellDate.getTime() < today.getTime();
    cells.push({
      day: d,
      available: isMonthAvailable && !isPast,
      season: seasonForView,
      isPast,
    });
  }

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
    setSelectedDate(null);
  };

  const handleBookingClick = () => {
    navigate("/booking-consultation-center", {
      state: {
        trekName: trek?.name,
        selectedPackage: "All-Inclusive Package",
        preferredDate: selectedDate
          ? `${MONTHS_NAMES[viewMonth]} ${selectedDate}, ${viewYear}`
          : undefined,
      },
    });
  };

  const legendSeasons = [
    ...new Set(availableMonths.map(getSeason).filter(Boolean)),
  ];

  return (
    <div className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            Book Your <span className="text-accent">Adventure</span>
          </h2>
          <div className="w-16 h-1 bg-accent rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Everything you need for an unforgettable Himalayan experience
            &mdash; all in one comprehensive package.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* All-Inclusive Package */}
            <div>
              <h3 className="font-headline text-2xl font-semibold text-foreground mb-5">
                All-Inclusive Package
              </h3>
              <div className="bg-[#fafaf7] rounded-2xl border border-border/50 p-6 md:p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h4 className="font-headline text-xl font-bold text-foreground">
                      Complete Himalayan Trek Experience
                    </h4>
                    <p className="text-text-secondary text-sm mt-1">
                      Every service included &mdash; from expert guidance to premium comfort
                    </p>
                  </div>
                  <div className="bg-accent/10 px-3 py-1.5 rounded-full flex-shrink-0">
                    <span className="text-accent text-sm font-semibold">Best Value</span>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  {mergedFeatures.map((f, i) => (
                    <div key={i} className="flex items-start gap-3 p-2.5 bg-white rounded-xl">
                      <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                        <Icon name={f.icon} size={16} className="text-success" />
                      </div>
                      <span className="text-sm text-text-secondary pt-1 leading-relaxed">
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Preferred Departure Date */}
            <div>
              <h3 className="font-headline text-2xl font-semibold text-foreground mb-5">
                Preferred Departure Date
              </h3>
              <div className="bg-[#fafaf7] rounded-2xl border border-border/50 p-6 md:p-8">
                <div className="flex items-center justify-between mb-5">
                  <button
                    onClick={prevMonth}
                    className="w-9 h-9 rounded-xl hover:bg-white border border-border/50 flex items-center justify-center text-text-secondary hover:text-foreground transition-colors"
                    aria-label="Previous month"
                  >
                    <Icon name="ChevronLeft" size={18} />
                  </button>
                  <span className="font-headline font-semibold text-lg text-foreground">
                    {MONTHS_NAMES[viewMonth]} {viewYear}
                  </span>
                  <button
                    onClick={nextMonth}
                    className="w-9 h-9 rounded-xl hover:bg-white border border-border/50 flex items-center justify-center text-text-secondary hover:text-foreground transition-colors"
                    aria-label="Next month"
                  >
                    <Icon name="ChevronRight" size={18} />
                  </button>
                </div>

                {viewSeasonStyle && isMonthAvailable && (
                  <div className={`${viewSeasonStyle.bg} ${viewSeasonStyle.text} text-sm font-medium px-3 py-1.5 rounded-lg mb-4 inline-flex items-center gap-1.5`}>
                    <span className={`w-2 h-2 rounded-full ${viewSeasonStyle.dot}`} />
                    {viewSeasonStyle.label} &mdash; Recommended for trekking
                  </div>
                )}
                {!isMonthAvailable && (
                  <div className="bg-gray-100 text-gray-400 text-sm font-medium px-3 py-1.5 rounded-lg mb-4 inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-gray-300" />
                    Not available this month
                  </div>
                )}

                <div className="grid grid-cols-7 gap-1">
                  {WEEKDAYS.map((wd) => (
                    <div key={wd} className="text-center text-xs font-semibold text-text-secondary py-2">
                      {wd}
                    </div>
                  ))}
                  {cells.map((cell, i) =>
                    cell ? (
                      <button
                        key={i}
                        onClick={() => setSelectedDate(cell.day === selectedDate ? null : cell.day)}
                        disabled={!cell.available}
                        className={`relative aspect-square rounded-lg text-sm font-medium transition-all duration-150 ${
                          cell.available
                            ? [
                                "cursor-pointer",
                                viewSeasonStyle?.hover,
                                selectedDate === cell.day
                                  ? "bg-primary text-white ring-2 ring-primary ring-offset-1"
                                  : `${viewSeasonStyle?.bg} ${viewSeasonStyle?.text} hover:shadow-sm`,
                              ].join(" ")
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                      >
                        {cell.day}
                      </button>
                    ) : (
                      <div key={i} />
                    ),
                  )}
                </div>

                {legendSeasons.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-border/50 flex flex-wrap gap-4">
                    {legendSeasons.map((s) => {
                      const st = SEASON_STYLES[s];
                      return (
                        <div key={s} className="flex items-center gap-2 text-xs text-text-secondary">
                          <div className={`w-2.5 h-2.5 rounded-full ${st.dot}`} />
                          <span>{st.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Booking Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#fafaf7] rounded-2xl border border-border/50 p-6 sticky top-24 shadow-sm">
              <h3 className="font-headline text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                <Icon name="FileText" size={18} className="text-accent" />
                Booking Summary
              </h3>

              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between py-3 border-b border-border/50">
                  <span className="text-sm text-text-secondary">Trek:</span>
                  <span className="font-medium text-foreground text-sm text-right">{trek?.name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border/50">
                  <span className="text-sm text-text-secondary">Duration:</span>
                  <span className="font-medium text-foreground text-sm">{trek?.duration} days</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-border/50">
                  <span className="text-sm text-text-secondary">Package:</span>
                  <span className="font-medium text-foreground text-sm">All-Inclusive</span>
                </div>
                {selectedDate && (
                  <div className="flex items-center justify-between py-3 border-b border-border/50">
                    <span className="text-sm text-text-secondary">Preferred date:</span>
                    <span className="font-medium text-foreground text-sm text-right">
                      {MONTHS_NAMES[viewMonth]} {selectedDate}, {viewYear}
                    </span>
                  </div>
                )}

              </div>

              <div className="space-y-3">
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  className="btn-conversion"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={handleBookingClick}
                >
                  Request Booking
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={handleBookingClick}
                >
                  Free Consultation
                </Button>
              </div>

              <div className="mt-8 pt-6 border-t border-border/50 space-y-3">
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Icon name="Shield" size={16} className="text-success" />
                  <span>100% Secure Process</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Icon name="RefreshCw" size={16} className="text-success" />
                  <span>Flexible Cancellation Policy</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Icon name="Award" size={16} className="text-success" />
                  <span>Licensed & Experienced Operators</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSection;
